import { useState } from "react";
import { Link } from "react-router-dom";
import {
    Lightning,
    ArrowLeft,
    ArrowRight,
    CaretDown,
    CheckCircle,
    Trophy,
    Robot,
    Books,
    Rocket,
    Clock,
    Money,
    Brain,
    Target,
    FlowArrow,
    PaintBrush,
    Crown,
    WarningCircle,
    Sparkle,
    Megaphone,
    ChartLineUp,
} from "@phosphor-icons/react";

/**
 * Comprehensive FAQ + Operator Guide page.
 * Built for high-intent prospects who want to understand exactly what they get
 * before claiming a seat. Linked from Landing → "Real operators. Real results." section.
 */

const TOOLS = [
    {
        icon: Books,
        title: "The Recipe Vault — 12 Proven Niche Blueprints",
        body: "Step-by-step PDFs for the niches that actually print right now. Vanished Empire (history). Tactical Titan (military/tech). Canine Connection (pets). Each Recipe gives you the exact niche positioning, audience psychology, hook formulas, monetization paths (TikTok Creativity Program, AdSense, affiliates, sponsorships), and content cadence. Stop guessing — pick a Recipe and run it.",
    },
    {
        icon: Brain,
        title: "Nexus Script Engine (Gemini 3 Flash AI)",
        body: "Drop in any topic. Get back a fully-structured 60-second video script in under 10 seconds — with hooks, story beats, B-roll cues, voice-over notes, and a viral CTA built in. No prompt engineering. No staring at ChatGPT wondering what to type. Just a topic in, a script out, ready to record.",
    },
    {
        icon: PaintBrush,
        title: "The Visual Authority Guide ($47 Order Bump)",
        body: "Headlines & thumbnails are 80% of the battle. This guide hands you the Mad-Lib headline swipe file (Forbidden Knowledge / Personal Transformation / FOMO categories), the Vegas-Tech thumbnail psychology framework (Extreme Emotion / Rule of Two / Color Signal), the 2026 SEO Signature, and the Nexus Deployment Checklist.",
    },
    {
        icon: Rocket,
        title: "The Deployment Hub — Platform 101 Rapid Guides",
        body: "Branded launch playbooks for the three platforms that matter: TikTok Creativity Program Unlock (10K formula, 1-Minute Rule, Business Account checklist), YouTube Faceless SEO (keyword deployment, CTR vs AVD math), and Instagram Reels-to-Revenue (Auto-DM funnels via ManyChat, single-keyword triggers, lead-magnet strategy).",
    },
    {
        icon: Megaphone,
        title: "Growth Playbook — Marketing & Engagement Bibles",
        body: "The Creator Launchpad Marketing Guide. Best Times to Post in 2026 (Global Edition). Engagement Hooks That Double Your Comments. Real, applied marketing strategy — not vague theory. Drop-in tactics you can deploy this afternoon.",
    },
    {
        icon: Trophy,
        title: "Live Leaderboard + Founder Cohort Network",
        body: "See where you rank against every other Operator. Compete, learn, climb. Plus the Feedback Lounge — drop your first upload, get tactical feedback from Operators who are also running the system. A war room, not a chat room.",
    },
    {
        icon: ChartLineUp,
        title: "Alpha Feed — Weekly Trend Drops",
        body: "Curated alerts on what's exploding RIGHT NOW. New monetization opportunities. Algorithm shifts. Niche openings before they get saturated. The intel layer that keeps you ahead instead of chasing trends a week late.",
    },
    {
        icon: FlowArrow,
        title: "Prompt Lab — Battle-Tested AI Prompts",
        body: "Copy-paste prompt library for hooks, captions, B-roll ideas, comment replies, even DM funnels. Already engineered, already tested. Ten years of prompt engineering compressed into a copy button.",
    },
];

const FAQS = [
    {
        q: "I have ZERO experience with video, AI, or business. Will this still work for me?",
        a: "Yes — that's exactly who Nexus.Lab is built for. Every Recipe is a paint-by-numbers blueprint. The Script Engine writes for you. The thumbnails follow a template. The marketing is laid out step by step. If you can copy + paste and read English, you can run this system. The Operators in the case studies came from office management, retail, college, and military trade backgrounds. None of them had 'creator' on their resume before joining.",
    },
    {
        q: "I don't want to show my face on camera. Can I still build a real channel?",
        a: "That's the entire point. Nexus.Lab is built for FACELESS creators. You use AI voice-overs (free tools like ElevenLabs free tier, Capcut built-in, or just text-to-speech). You use stock B-roll, AI-generated visuals, archival footage. Most of your top niches (history, tactical, animation explainers, pet content) work BETTER without a face. You stay anonymous, you build the asset, you cash the checks.",
    },
    {
        q: "How much time per day do I actually need?",
        a: "Sarah J. spent 45 minutes/day. Jerry M. spent 6 hours/week. Bailey R. fit it around college classes. The Assembly Line is designed so that one Recipe + Script Engine + the Deployment Hub gets you from 'topic idea' to 'uploaded video' in under 90 minutes. Once you have your workflow down, it's faster than scrolling Instagram for the same amount of time.",
    },
    {
        q: "How fast can I expect to see income?",
        a: "We will not promise overnight money — that's a scam tell. What we WILL tell you is what our Operators actually did: Sarah hit her first $500 profit month in Month 1. Jerry crossed $1,700/mo by Month 3. Bailey hit $2,150 in Month 2 after one viral video. The pattern is consistent: 30-60 days of disciplined Recipe execution before the algorithm starts compounding for you.",
    },
    {
        q: "Why is the timing right NOW?",
        a: "AI just hit a tipping point in November 2025. Gemini 3 + GPT-5 + nano-banana image gen + Sora 2 video gen mean a single faceless creator can now produce content at the quality and speed that previously took a 5-person agency. Combine that with the TikTok Creativity Program paying real money + YouTube's faceless-friendly algorithm shift + the affiliate boom — there's a 12-18 month window where solo Operators can build six-figure assets before this gets crowded. We give you the exact playbooks for that window.",
    },
    {
        q: "What's the difference between Founder ($20/mo) and Lifetime ($497)?",
        a: "Founder = $20/month, locked at that rate forever (rate goes up to $40+/mo for non-founders after Seat #1,000 sells out). Cancel anytime. Lifetime = $497 once, never billed again, ever. Plus a permanent LIFETIME FOUNDER badge on the leaderboard, priority access to new Recipe drops 1 week before everyone else, and direct support from Steve. If you're confident this is your move for the next 24+ months, Lifetime is the smarter math. If you want to test the waters, Founder gets you in the door.",
    },
    {
        q: "What happens at Seat #1,000?",
        a: "The Founder Cohort closes. We stop accepting new members until 2027 (or longer). At that point, the only way in is the waitlist — and the rate will be significantly higher. Right now we're at 96.5% capacity. 35 seats remain.",
    },
    {
        q: "What if I claim a seat and it's not for me?",
        a: "Founder tier — cancel anytime from your dashboard, you keep access through the end of your billing period. Zero questions asked. Lifetime tier — 14-day refund window from purchase date if you decide it's not the right fit. Email contact@nexuscreatorlab.com.",
    },
    {
        q: "How is this different from the 100 other 'creator courses' on the internet?",
        a: "Three differences. (1) This is NOT a course you watch once and forget. It's an ACTIVE platform with weekly drops, live leaderboard, AI tools, and an Operator network. (2) The Recipes are PROVEN — every one is reverse-engineered from a real channel doing real numbers, not theory. (3) We don't sell hype. We sell systems. You won't find any 'I made $50K my first week' lies here.",
    },
    {
        q: "Do I need expensive software, cameras, or equipment?",
        a: "No. The minimum viable stack: laptop + free Capcut + free ElevenLabs voice + free Pexels/Unsplash B-roll. Total cost to start: $0 beyond your Nexus membership. As you scale, you can upgrade — but you do NOT need to spend a dime on tools to run Recipe #001 today.",
    },
];

const SAVED_BY_NEXUS = [
    { metric: "$5,000+", label: "saved on creator coaching/courses" },
    { metric: "200+ hours", label: "saved researching niches & strategies" },
    { metric: "6-12 months", label: "of guesswork eliminated" },
    { metric: "$0", label: "spent on equipment to start" },
];

const STORIES = {
    sarah: {
        name: "Sarah J.",
        emoji: "history",
        recipe: "Recipe #001 — The Vanished Empire",
        before: "Full-time office manager in suburban Texas. Burned out, scrolling TikTok at midnight, watching other people make money she couldn't figure out how to replicate.",
        howShe: [
            "Picked Recipe #001 (Lost Tech of Ancient Egypt) on her first night as a member.",
            "Recorded 3 voice-overs using ElevenLabs free tier + stock archival footage from Pexels.",
            "Used the Nexus Script Engine to generate the entire script in under 10 minutes per video.",
            "Followed the Visual Authority Guide for thumbnails — Forbidden Knowledge headline category, Rule of Two color contrast.",
            "Posted 4 videos in Week 1, 5 in Week 2, kept the cadence.",
        ],
        result: "Her THIRD video hit 450K views. Month 1 profit: $500 from TikTok Creativity Program + Egypt-themed Amazon affiliate links. By Month 2 she had a 15K-follower asset valued at $1,500+ in a private channel sale comp.",
        timeInvested: "45 minutes per night, 5 nights a week.",
        moneyOut: "$0 on equipment. $20 Founder membership.",
    },
    jerry: {
        name: "Jerry M.",
        emoji: "tactical",
        recipe: "Recipe #008 — The Tactical Titan",
        before: "Retired trade professional, late 50s, never made a video before in his life. Had decades of military and engineering knowledge sitting unused.",
        howShe: [
            "Picked Recipe #008 (drone engineering breakdowns) — his exact lane.",
            "Used the Script Engine to convert his expertise into 60-second hooks the algorithm rewards.",
            "Used the Visual Authority Guide's Tactical thumbnail formula — high-contrast military gear shots.",
            "Followed the Deployment Hub's YouTube Faceless SEO playbook to lock in keyword authority on 'drone engineering'.",
            "Layered in tactical-gear affiliate links + applied for a tactical sponsorship via the Marketing Guide cold-pitch template.",
        ],
        result: "Crossed 100,000 views per video at a PREMIUM $12 RPM (tactical/military niches pay 3-5x more than entertainment). Month 3: $1,700/mo from AdSense + sponsorship.",
        timeInvested: "6 hours per week. Records on Saturday mornings.",
        moneyOut: "$0 on equipment. $497 Lifetime membership.",
    },
    bailey: {
        name: "Bailey R.",
        emoji: "pets",
        recipe: "Recipe #005 — The Canine Connection",
        before: "College sophomore, broke, working a part-time campus job. Loved her dog. That was it.",
        howShe: [
            "Picked Recipe #005 (emotional pet content) — leaned into what she actually loved.",
            "Used the Script Engine to write 'POV: your dog is your best friend' style hooks.",
            "Followed the Engagement Hooks Playbook to engineer comment-bait questions in every caption.",
            "Used the Instagram Reels-to-Revenue playbook to set up a ManyChat auto-DM funnel — when viewers commented a single keyword, an Amazon dog-product affiliate link auto-DM'd them.",
            "Posted in the Feedback Lounge for tactical feedback after Week 1.",
        ],
        result: "Her sixth video went MEGA-VIRAL: 2.4M views in 72 hours. The auto-DM funnel converted 4,800 affiliate clicks. Month 2 profit: $2,150 from TikTok Creativity Program + Amazon commissions.",
        timeInvested: "Fit around college classes. Roughly 1 hour per video.",
        moneyOut: "$0 on equipment. $20 Founder membership.",
    },
};

function FaqItem({ q, a, idx }) {
    const [open, setOpen] = useState(false);
    return (
        <div
            data-testid={`faq-item-${idx}`}
            className={`border ${open ? "border-electric-blue" : "border-white/10"} bg-[#0A0A0A] transition-all`}
        >
            <button
                onClick={() => setOpen(!open)}
                data-testid={`faq-toggle-${idx}`}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-white/5 transition-colors"
            >
                <span className="font-heading font-bold text-base md:text-lg text-white">{q}</span>
                <CaretDown
                    size={18}
                    weight="bold"
                    className={`text-electric-blue flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>
            {open && (
                <div className="px-5 pb-5 pt-1 text-sm md:text-base text-foreground/80 leading-relaxed border-t border-white/5">
                    {a}
                </div>
            )}
        </div>
    );
}

function StoryCard({ story, testId }) {
    return (
        <div data-testid={testId} className="bg-[#0A0A0A] border border-white/10 hover:border-electric-blue/40 transition-all p-6 md:p-8">
            <div className="flex items-center gap-3 mb-2">
                <Trophy size={18} weight="fill" className="text-electric-blue" />
                <p className="label-mono text-electric-blue text-xs">{story.recipe}</p>
            </div>
            <h3 className="font-heading text-3xl md:text-4xl font-black tracking-tight mb-5">{story.name}</h3>

            <p className="label-mono text-[10px] text-muted-foreground mb-2">BEFORE NEXUS</p>
            <p className="text-sm text-foreground/80 leading-relaxed mb-6 italic">{story.before}</p>

            <p className="label-mono text-[10px] text-electric-blue mb-3">EXACTLY WHAT THEY DID</p>
            <ol className="space-y-2 mb-6 list-decimal list-outside pl-5">
                {story.howShe.map((step, i) => (
                    <li key={`${story.name}-step-${i}`} className="text-sm text-foreground/85 leading-relaxed">{step}</li>
                ))}
            </ol>

            <div className="border-t border-electric-blue/20 pt-5">
                <p className="label-mono text-[10px] text-electric-blue mb-2">RESULT</p>
                <p className="text-sm md:text-base text-white leading-relaxed mb-4 font-medium">{story.result}</p>
                <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="bg-black/40 border border-white/5 p-3">
                        <p className="label-mono text-[9px] text-muted-foreground mb-1">TIME INVESTED</p>
                        <p className="text-foreground/90">{story.timeInvested}</p>
                    </div>
                    <div className="bg-black/40 border border-white/5 p-3">
                        <p className="label-mono text-[9px] text-muted-foreground mb-1">MONEY OUT</p>
                        <p className="text-foreground/90">{story.moneyOut}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function FAQ() {
    return (
        <div className="min-h-screen bg-background relative">
            {/* Header */}
            <header className="border-b border-white/10 px-6 md:px-12 py-4 flex justify-between items-center">
                <Link to="/" data-testid="faq-logo" className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-electric-blue flex items-center justify-center neon-border">
                        <Lightning weight="fill" size={18} className="text-white" />
                    </div>
                    <span className="font-heading font-black tracking-tighter text-lg">
                        NEXUS<span className="text-electric-blue">.</span>LAB
                    </span>
                </Link>
                <Link
                    to="/"
                    data-testid="faq-back-link"
                    className="label-mono text-xs hover:text-electric-blue flex items-center gap-1.5 transition-colors"
                >
                    <ArrowLeft size={12} weight="bold" /> BACK TO LANDING
                </Link>
            </header>

            {/* HERO */}
            <section className="relative max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28">
                <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
                <div className="relative">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-electric-blue/40 bg-electric-blue/10 mb-6">
                        <Sparkle size={14} weight="fill" className="text-electric-blue" />
                        <span className="label-mono text-[10px] text-electric-blue">OPERATOR'S GUIDE · FAQ · CASE STUDIES</span>
                    </div>
                    <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.02] mb-6">
                        Everything you get<br />
                        <span className="text-electric-blue neon-text">when you claim a seat.</span>
                    </h1>
                    <p className="text-lg md:text-xl text-foreground/80 leading-relaxed max-w-3xl mb-8">
                        Below is the complete, no-spin breakdown: the exact tools you'll have access to,
                        what real Operators did to make money, every question we get asked,
                        and why the next 12 months is the highest-leverage window of the decade for faceless creators.
                    </p>
                    <Link
                        to="/signup"
                        data-testid="faq-hero-cta"
                        className="inline-flex items-center gap-2 bg-electric-blue hover:bg-electric-blue-dark text-white font-bold px-8 py-4 text-sm tracking-wider uppercase transition-all hover:shadow-[0_0_32px_rgba(0,123,255,0.7)]"
                    >
                        CLAIM YOUR SEAT — $20/MO LOCKED
                        <ArrowRight size={18} weight="bold" />
                    </Link>
                </div>
            </section>

            {/* TIMING / WHY NOW */}
            <section className="relative max-w-5xl mx-auto px-6 md:px-12 py-20">
                <div className="border border-electric-blue/30 bg-gradient-to-br from-[#0A0A0A] to-[#0A1428] p-8 md:p-12 relative overflow-hidden">
                    <div className="absolute -top-12 -right-12 w-72 h-72 bg-electric-blue/10 rounded-full blur-3xl pointer-events-none" />
                    <p className="label-mono text-electric-blue mb-3">WHY NOW · THE 12-MONTH WINDOW</p>
                    <h2 className="font-heading text-3xl md:text-5xl font-black tracking-tighter leading-[1.05] mb-6">
                        AI just collapsed the cost of producing content<br />
                        <span className="text-electric-blue">to almost zero.</span>
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-foreground/80 leading-relaxed">
                        <p>
                            What used to take a 5-person agency a full week — researching, scripting, voiceover,
                            B-roll, thumbnails, scheduling — now takes ONE person and 90 minutes.
                            Gemini 3, GPT-5, nano-banana image gen, Sora 2 video gen, ElevenLabs voice cloning —
                            it all hit in late 2025.
                        </p>
                        <p>
                            <span className="text-white font-bold">Two truths sit on top of that.</span>{" "}
                            (1) The TikTok Creativity Program, YouTube AdSense for faceless niches, and
                            affiliate-driven Reels are paying real money RIGHT NOW. (2) The window is
                            12-18 months before this gets saturated. Solo Operators who deploy the system
                            this year are the ones building six-figure digital assets. Operators who wait
                            until 2027 are competing against everyone who didn't.
                        </p>
                    </div>
                </div>
            </section>

            {/* WHAT YOU GET */}
            <section className="relative max-w-6xl mx-auto px-6 md:px-12 py-20">
                <div className="text-center mb-12">
                    <p className="label-mono mb-3 text-electric-blue">EXACTLY WHAT YOU GET</p>
                    <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter leading-[1.05] mb-4">
                        8 tools. 1 platform. <span className="text-electric-blue">Zero guessing.</span>
                    </h2>
                    <p className="text-muted-foreground max-w-2xl mx-auto">
                        These aren't bonuses. These aren't upsells. Every Operator gets the full stack the moment they claim their seat.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {TOOLS.map((tool, idx) => {
                        const Icon = tool.icon;
                        return (
                            <div
                                key={tool.title}
                                data-testid={`tool-${idx}`}
                                className="bg-[#0A0A0A] border border-white/10 hover:border-electric-blue/40 transition-all p-6 group"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0 w-11 h-11 bg-electric-blue/10 border border-electric-blue/30 flex items-center justify-center group-hover:bg-electric-blue/20 transition-all">
                                        <Icon size={20} weight="fill" className="text-electric-blue" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-heading font-black text-lg leading-tight mb-2">{tool.title}</h3>
                                        <p className="text-sm text-foreground/75 leading-relaxed">{tool.body}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* SAVINGS */}
            <section className="relative max-w-6xl mx-auto px-6 md:px-12 py-20">
                <div className="text-center mb-12">
                    <p className="label-mono mb-3 text-electric-blue">WHAT NEXUS REPLACES</p>
                    <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter leading-[1.05] mb-4">
                        Stop spending on courses.<br />
                        <span className="text-electric-blue">Start running a system.</span>
                    </h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                    {SAVED_BY_NEXUS.map((item, idx) => (
                        <div key={item.label} data-testid={`savings-${idx}`} className="bg-[#0A0A0A] border border-electric-blue/20 p-5 md:p-6 text-center">
                            <p className="font-heading text-3xl md:text-4xl font-black text-electric-blue mb-2 leading-none">{item.metric}</p>
                            <p className="text-xs text-muted-foreground leading-snug">{item.label}</p>
                        </div>
                    ))}
                </div>
                <p className="text-center text-xs text-muted-foreground mt-6 italic max-w-xl mx-auto">
                    Average creator spends $300-$2,000 on courses, $50-$200/mo on tools, and 6+ months guessing before earning a dollar. Nexus.Lab compresses all of that into $20/mo.
                </p>
            </section>

            {/* DETAILED CASE STUDIES */}
            <section className="relative max-w-6xl mx-auto px-6 md:px-12 py-20">
                <div className="text-center mb-12">
                    <p className="label-mono mb-3 text-electric-blue">CASE STUDIES · THE FULL STORY</p>
                    <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter leading-[1.05] mb-4">
                        Three real Operators.<br />
                        <span className="text-electric-blue">The exact playbook each ran.</span>
                    </h2>
                    <p className="text-muted-foreground max-w-2xl mx-auto">
                        We don't sell hype. We show receipts. Below is what each Operator did, step-by-step, including the time invested and money spent on tools.
                    </p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <StoryCard story={STORIES.sarah} testId="case-study-detail-sarah" />
                    <StoryCard story={STORIES.jerry} testId="case-study-detail-jerry" />
                    <StoryCard story={STORIES.bailey} testId="case-study-detail-bailey" />
                </div>
                <p className="text-center text-xs text-muted-foreground mt-8 italic max-w-2xl mx-auto">
                    Income claims represent results from Founding Cohort Operators based on direct member reporting.
                    Individual results vary based on time invested, niche selection, and execution. Nothing on this
                    page is a guarantee of income.
                </p>
            </section>

            {/* FAQ ACCORDION */}
            <section className="relative max-w-3xl mx-auto px-6 md:px-12 py-20">
                <div className="text-center mb-10">
                    <p className="label-mono mb-3 text-electric-blue">QUESTIONS WE GET ASKED</p>
                    <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter leading-[1.05]">
                        Everything you'd ask <span className="text-electric-blue">before</span> claiming a seat.
                    </h2>
                </div>
                <div className="space-y-3">
                    {FAQS.map((f, idx) => (
                        <FaqItem key={f.q} q={f.q} a={f.a} idx={idx} />
                    ))}
                </div>
            </section>

            {/* URGENCY / FOMO */}
            <section className="relative max-w-5xl mx-auto px-6 md:px-12 py-20">
                <div className="border-2 border-electric-blue bg-[#0A0A0A] p-8 md:p-12 relative overflow-hidden neon-border">
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-electric-blue to-transparent" />
                    <div className="absolute -top-12 -right-12 w-64 h-64 bg-electric-blue/15 rounded-full blur-3xl pointer-events-none" />

                    <div className="relative">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-electric-blue/40 bg-electric-blue/10 mb-5">
                            <WarningCircle size={14} weight="fill" className="text-electric-blue" />
                            <span className="label-mono text-[10px] text-electric-blue">FOUNDER COHORT · 96.5% CAPACITY</span>
                        </div>
                        <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02] mb-5">
                            35 seats remain.<br />
                            <span className="text-electric-blue neon-text">Then the door closes until 2027.</span>
                        </h2>
                        <ul className="space-y-3 mb-8 max-w-2xl">
                            <li className="flex items-start gap-3 text-sm md:text-base text-foreground/85">
                                <CheckCircle size={18} weight="fill" className="text-electric-blue mt-0.5 flex-shrink-0" />
                                <span><strong className="text-white">$20/mo locked forever</strong> — non-Founder rate jumps to $40+/mo after Seat #1,000</span>
                            </li>
                            <li className="flex items-start gap-3 text-sm md:text-base text-foreground/85">
                                <CheckCircle size={18} weight="fill" className="text-electric-blue mt-0.5 flex-shrink-0" />
                                <span><strong className="text-white">FOUNDER badge</strong> on the leaderboard — permanent status only earned in this cohort</span>
                            </li>
                            <li className="flex items-start gap-3 text-sm md:text-base text-foreground/85">
                                <CheckCircle size={18} weight="fill" className="text-electric-blue mt-0.5 flex-shrink-0" />
                                <span><strong className="text-white">Cancel anytime</strong> — keep access through end of billing period, zero questions</span>
                            </li>
                            <li className="flex items-start gap-3 text-sm md:text-base text-foreground/85">
                                <CheckCircle size={18} weight="fill" className="text-electric-blue mt-0.5 flex-shrink-0" />
                                <span><strong className="text-white">Full access today</strong> — every Recipe, Playbook, AI tool, and the Operator network unlocks the moment you join</span>
                            </li>
                        </ul>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <Link
                                to="/signup"
                                data-testid="faq-bottom-claim-cta"
                                className="inline-flex items-center justify-center gap-2 bg-electric-blue hover:bg-electric-blue-dark text-white font-bold px-8 py-4 text-sm tracking-wider uppercase transition-all hover:shadow-[0_0_32px_rgba(0,123,255,0.7)] animate-glow-pulse"
                            >
                                <Crown size={18} weight="fill" />
                                CLAIM A FOUNDER SEAT
                                <ArrowRight size={18} weight="bold" />
                            </Link>
                            <Link
                                to="/#pricing"
                                data-testid="faq-bottom-pricing-cta"
                                className="inline-flex items-center justify-center gap-2 border border-electric-blue text-electric-blue hover:bg-electric-blue hover:text-white px-8 py-4 text-sm tracking-wider uppercase transition-all"
                            >
                                SEE PRICING TIERS
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-white/10 px-6 md:px-12 py-8 max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
                    <p>© 2026 Nexus Creative Group LLC · Built for faceless operators.</p>
                    <div className="flex items-center gap-6">
                        <Link to="/terms" className="hover:text-electric-blue transition-colors">Terms</Link>
                        <Link to="/privacy" className="hover:text-electric-blue transition-colors">Privacy</Link>
                        <a href="mailto:contact@nexuscreatorlab.com" className="hover:text-electric-blue transition-colors">Contact</a>
                    </div>
                </div>
            </footer>
        </div>
    );
}
